import type { Entity } from './api.js';

/**
 * **Le lien vers une photo, dans son partage** *(L1, 2026-09-14)*.
 *
 * ### Pourquoi le partage fait partie de l'adresse
 *
 * Un lien vers la photo seule ouvrirait la visionneuse sur rien : la refermer
 * laisserait le destinataire à l'accueil ou dans son propre espace, sans savoir
 * **dans quel partage** il était — c'est l'objection de l'utilisateur, et elle
 * est juste. L'adresse désigne donc la photo **dans un partage** : le site ouvre
 * le partage, puis la visionneuse par-dessus. Fermer ramène au partage, et
 * remonter au groupe (`parentOf`).
 *
 * C'est aussi le partage qui **donne l'accès** : un destinataire lit la photo
 * parce qu'il est membre d'un cercle auquel ce partage est exposé. Nommer le
 * partage, c'est nommer la raison pour laquelle le lien s'ouvre.
 *
 * ### Le lien reste privé (décision ②)
 *
 * Il n'ouvre rien à qui n'a pas de compte et l'accès : aucune route anonyme,
 * aucun aperçu public, I-20 intact. C'est la **miniature jointe** à l'envoi qui
 * rend l'aperçu inutile.
 *
 * ### L'adresse est lue une fois, puis oubliée
 *
 * Au démarrage, le site la met de côté et revient à `/` : un rechargement une
 * heure plus tard ne doit pas rouvrir la photo du lien, et le mémo d'espace
 * (`synkara.space`) ne se contredit plus avec elle. La mise de côté vit en
 * `sessionStorage`, ce qui lui fait **traverser la connexion** — la redirection
 * vers Cognito reste dans le même onglet —, et c'est le cas normal : un lien
 * s'ouvre presque toujours dans un navigateur sans session.
 */

export interface LinkTarget {
  readonly aid: string;
  readonly fid: string;
}

const ULID = '[0-9A-HJKMNP-TV-Z]{26}';
const PATH = new RegExp(`^/partage/(${ULID})/(${ULID})/?$`, 'i');
const KEY = 'synkara.link';

export const linkUrl = (origin: string, target: LinkTarget): string =>
  `${origin}/partage/${target.aid}/${target.fid}`;

/** L'adresse d'un lien, ou `undefined` pour toute autre adresse. */
export function parseLink(pathname: string): LinkTarget | undefined {
  const m = PATH.exec(pathname);
  return m ? { aid: m[1]!.toUpperCase(), fid: m[2]!.toUpperCase() } : undefined;
}

/**
 * Met le lien de côté et rend `/` à la barre d'adresse. **Tout stockage peut
 * lever** (navigation privée, stockage bloqué) : le lien est alors perdu, pas la
 * page.
 */
export function keepLink(target: LinkTarget): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(target));
  } catch {
    // Rien à faire : l'accueil s'ouvrira normalement.
  }
  history.replaceState(null, '', '/');
}

/** Le lien mis de côté, consommé une seule fois. */
export function takeLink(): LinkTarget | undefined {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as Partial<LinkTarget>;
    return typeof v.aid === 'string' && typeof v.fid === 'string'
      ? parseLink(`/partage/${v.aid}/${v.fid}`)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * **Ce qu'on joint au lien** : la miniature, prête. Une photo ou une vidéo dont
 * le dérivé `thumb` existe — l'affiche, pour une vidéo. Environ 20 Ko contre
 * ~670 Ko pour `standard` : c'est le but du geste, une messagerie qui verse tout
 * dans la pellicule n'y verse presque rien.
 */
export function linkableOf(entity: Entity): { mime: string; ext: string } | undefined {
  if (entity.Type !== 'MEDIA' || !entity.Fid) return undefined;
  const thumb = entity.Variants?.['thumb'];
  return thumb?.size === undefined ? undefined : { mime: thumb.mime, ext: thumb.ext };
}

/** Un partage où le lien peut mener, avec son nom pour le choix. */
export interface LinkChoice {
  readonly aid: string;
  readonly name: string;
}

/**
 * **Vers quels partages le lien peut-il mener ?** — décidé sans un appel.
 *
 * - On regarde **un partage** : c'est lui, et lui seul. C'est le cas naturel, et
 *   il vaut aussi pour un membre qui n'est pas propriétaire de la photo.
 * - On regarde **son espace** : les partages où la photo est rangée. `albums`
 *   n'est servi qu'au propriétaire ; sur une arête, il manque, et la fiche du
 *   miroir le porte (`mirror`). Un partage **sans cercle** est écarté — personne
 *   d'autre que soi ne pourrait ouvrir le lien.
 *
 * Une liste vide veut dire : **l'entrée n'apparaît pas**. Un seul partage part
 * sans question ; plusieurs se choisissent.
 */
export function linkChoices(
  entity: Entity,
  viewing: string | undefined,
  known: readonly Entity[] | undefined,
  mirror?: Entity,
): readonly LinkChoice[] {
  const named = new Map((known ?? []).flatMap((a) => (a.Aid ? [[a.Aid, a] as const] : [])));
  if (viewing) return [{ aid: viewing, name: named.get(viewing)?.Name ?? '' }];

  const aids = entity.albums ?? mirror?.albums ?? [];
  return aids.flatMap((aid) => {
    const album = named.get(aid);
    return album && (album.authGroups?.length ?? 0) > 0 ? [{ aid, name: album.Name }] : [];
  });
}
